/**
 * Frontend-local LIMS shared types/constants.
 * Webpack aliases `@common/lims` here so Vercel builds (apps/frontend root)
 * do not depend on packages/common being present in the upload.
 * Keep in sync with packages/common/src/lims.ts for the symbols used by the FE.
 */

export const LAB_ROLES = ['lab_analyst', 'lab_releaser', 'lab_admin', 'lab_client'] as const;
export type LabRole = (typeof LAB_ROLES)[number];

export const METHOD_DISPOSITIONS = ['in_house', 'subcontract'] as const;
export type MethodDisposition = (typeof METHOD_DISPOSITIONS)[number];

export const BENCH_TEMPLATES = [
  'bod',
  'tss',
  'tss_influent',
  'nh3_ise',
  'tp_colorimetric',
  'cod_hr',
  'ecoli_quantitray',
  'dw_pa',
] as const;
export type BenchTemplate = (typeof BENCH_TEMPLATES)[number];

export const LIMS_SAMPLE_STATUSES = [
  'expected',
  'received',
  'rejected',
  'in_progress',
  'awaiting_sub',
  'ready',
  'released',
  'amended',
  'voided',
] as const;
export type LimsSampleStatus = (typeof LIMS_SAMPLE_STATUSES)[number];

export const LAB_ONBOARDING_STEPS = [
  'lab-profile',
  'methods',
  'team',
  'first-client',
  'finish',
] as const;
export type LabOnboardingStep = (typeof LAB_ONBOARDING_STEPS)[number];

export const STANDING_KIT_FREQUENCIES = ['weekly', 'monthly', 'quarterly', 'on_demand'] as const;
export type StandingKitFrequency = (typeof STANDING_KIT_FREQUENCIES)[number];

/** Letter-sheet bottle-label stock. Default is Avery 5162 / 5962 (1⅓″ × 4″, 14/sheet). */
export const LIMS_LABEL_SHEET_IDS = ['avery_5162', 'sheet_2x5'] as const;
export type LimsLabelSheetId = (typeof LIMS_LABEL_SHEET_IDS)[number];
export const DEFAULT_LIMS_LABEL_SHEET_ID: LimsLabelSheetId = 'avery_5162';

export const LIMS_LABEL_SHEETS: Array<{
  id: LimsLabelSheetId;
  label: string;
  labelsPerSheet: number;
}> = [
  {
    id: 'avery_5162',
    label: '1⅓″ × 4″ · 14/sheet (Avery 5162 / 5962)',
    labelsPerSheet: 14,
  },
  {
    id: 'sheet_2x5',
    label: '2×5 · 10/sheet (~1.87″ × 3.67″)',
    labelsPerSheet: 10,
  },
];

export function parseLimsLabelSheetId(raw: unknown): LimsLabelSheetId {
  const s = String(raw || '').trim();
  return (LIMS_LABEL_SHEET_IDS as readonly string[]).includes(s)
    ? (s as LimsLabelSheetId)
    : DEFAULT_LIMS_LABEL_SHEET_ID;
}

/** Flag column: P is subcontract, not a qualifier. */
export function displayResultFlag(flag: string | null | undefined): string {
  const f = String(flag || '').trim();
  if (!f || f.toUpperCase() === 'P') return '';
  return f;
}

/**
 * MDL column: the limit is named once in the header, so the cell stays numeric — same as the
 * COA. A reporting limit standing in for a method with no MDL is marked so it cannot be read
 * as a detection limit.
 */
export function limsLimitCell(r: { mdl?: unknown; rl?: unknown }): {
  text: string;
  reportingLimit: boolean;
} {
  const mdl = String(r.mdl ?? '').trim();
  if (mdl) return { text: mdl, reportingLimit: false };
  const rl = String(r.rl ?? '').trim();
  if (rl) return { text: `${rl} *`, reportingLimit: true };
  return { text: '—', reportingLimit: false };
}

export function isDerivedDateOnlyClock(source?: string | null): boolean {
  return String(source || '') === 'derived_date_only';
}

/** Date/time cell: observed clock vs date-only (time was not recorded). */
export function formatAnalysisWhenLabel(
  analysisAt: string | null | undefined,
  clockSource?: string | null,
  toLocal?: (iso: string) => string
): string {
  if (!analysisAt) return '—';
  if (isDerivedDateOnlyClock(clockSource)) {
    const d = new Date(analysisAt);
    if (!Number.isNaN(d.getTime())) {
      const pad = (n: number) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} · date only`;
    }
    return 'date only';
  }
  const local = toLocal ? toLocal(analysisAt) : String(analysisAt);
  return local.replace('T', ' ');
}

export type LimsSampleType = 'Grab' | 'Composite';

export type LimsStandingKitLine = {
  line: number;
  location: string;
  sampleType: LimsSampleType;
  requestedMethodIds: string[];
};

export function normalizeLimsSampleType(raw: unknown): LimsSampleType {
  return String(raw || '')
    .toLowerCase()
    .includes('composite')
    ? 'Composite'
    : 'Grab';
}

/** Allow a few minutes of clock skew when rejecting future receipt times. */
export const RECEIPT_FUTURE_SKEW_MS = 5 * 60 * 1000;

function coerceDate(value: unknown): Date | null {
  if (value == null || value === '') return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'object' && typeof (value as { toDate?: () => Date }).toDate === 'function') {
    try {
      const d = (value as { toDate: () => Date }).toDate();
      return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null;
    } catch {
      return null;
    }
  }
  const d = new Date(String(value));
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Receipt time is transcribed from the COC. Reject missing, invalid, future,
 * or pre-collection values — never substitute the server clock.
 * Keep in sync with packages/common/src/lims.ts.
 */
export function parseEnteredReceivedAt(
  raw: unknown,
  opts?: { collectedAt?: unknown; now?: Date }
): Date {
  const receivedAt = coerceDate(raw);
  if (!receivedAt) {
    const empty = raw == null || String(raw).trim() === '';
    throw new Error(
      empty
        ? 'Received date/time is required — enter the time written on the COC'
        : 'Received date/time is invalid'
    );
  }
  const now = opts?.now instanceof Date && !Number.isNaN(opts.now.getTime()) ? opts.now : new Date();
  if (receivedAt.getTime() > now.getTime() + RECEIPT_FUTURE_SKEW_MS) {
    throw new Error('Received date/time cannot be in the future');
  }
  const collectedAt = coerceDate(opts?.collectedAt);
  if (collectedAt && receivedAt.getTime() < collectedAt.getTime()) {
    throw new Error('Received date/time cannot be before collection');
  }
  return receivedAt;
}

/** Keep in sync with packages/common/src/lims.ts. */
export function normalizeStandingKitLines(kit: {
  lines?: LimsStandingKitLine[] | null;
  locations?: string[] | null;
  sampleType?: LimsSampleType | string;
  requestedMethodIds?: string[] | null;
}): LimsStandingKitLine[] {
  const fallbackIds = Array.isArray(kit.requestedMethodIds)
    ? kit.requestedMethodIds.map(String).filter(Boolean)
    : [];
  if (Array.isArray(kit.lines) && kit.lines.length) {
    return kit.lines
      .map((line, idx) => {
        const location = String(line?.location || '').trim();
        if (!location) return null;
        const ids = Array.isArray(line.requestedMethodIds) && line.requestedMethodIds.length
          ? line.requestedMethodIds.map(String).filter(Boolean)
          : fallbackIds;
        const n = Math.trunc(Number(line.line));
        return {
          line: Number.isFinite(n) && n > 0 ? n : idx + 1,
          location,
          sampleType: normalizeLimsSampleType(line.sampleType),
          requestedMethodIds: ids,
        } as LimsStandingKitLine;
      })
      .filter((x): x is LimsStandingKitLine => Boolean(x))
      .sort((a, b) => Number(a.line || 0) - Number(b.line || 0));
  }
  return (kit.locations || [])
    .map((loc) => String(loc || '').trim())
    .filter(Boolean)
    .map((location, idx) => ({
      line: idx + 1,
      location,
      sampleType: normalizeLimsSampleType(kit.sampleType),
      requestedMethodIds: fallbackIds,
    }));
}
